// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { lineEllipsisStyles } from "@store/Themes/common/mixins/typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    displayDynamicTitle = "displayDynamicTitle",
    links = "links",
    imageUrl = "imageUrl",
    iconColor = "iconColor",
    columns = "columns",
}

export interface QuickActionPillsProps extends WidgetProps {
    fields: {
        [fields.displayDynamicTitle]: boolean;
        [fields.columns]: string;
        [fields.links]: (LinkModel & {
            fields: {
                [fields.imageUrl]: string;
                [fields.iconColor]: string;
            };
        })[];
    };
}

export interface QuickActionPillsStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    pillsContainer?: InjectableCss;
    pill?: LinkPresentationProps;
    iconCircle?: InjectableCss;
    iconImg?: ImgProps;
    pillLabel?: InjectableCss;
}

export const quickActionPillsStyles: QuickActionPillsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: ${pxToRem(24)};
        `,
    },
    heading: {
        forwardAs: "h2",
        css: css`
            font-size: ${pxToRem(30)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            color: ${getColor("common.background")};
            line-height: 1.2;
            margin: 0;

            ${breakpoint("md")} {
                font-size: ${pxToRem(40)};
            }
        `,
    },
    pillsContainer: {
        css: css`
            display: grid;
            gap: ${pxToRem(12)};
            width: 100%;
            margin: 0;
            padding: 0;
            list-style: none;

            > li {
                min-width: 0;
                list-style: none;
            }
        `,
    },
    pill: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: ${pxToRem(4)};
            gap: ${pxToRem(8)};
            width: 100%;
            min-width: 0;
            min-height: ${pxToRem(44)};
            background-color: ${getColor("common.background")};
            border-radius: ${pxToRem(100)};
            color: ${getColor("text.main")};
            text-decoration: none;
            cursor: pointer;
            transition:
                box-shadow 0.2s ease,
                transform 0.2s ease,
                color 0.2s ease;

            &:hover,
            &:focus,
            &:focus-visible {
                box-shadow: 0 ${pxToRem(4)} ${pxToRem(12)} rgba(0, 0, 0, 0.2);
                transform: translateY(-${pxToRem(1)});
                text-decoration: none;
                color: ${getColor("primary.main")};
            }
        `,
        typographyProps: {
            css: css`
                display: contents;
            `,
        },
    },
    iconCircle: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: ${pxToRem(36)};
            height: ${pxToRem(36)};
            background-color: ${getColor("tertiary.light")};
            border: ${pxToRem(1)} solid rgba(255, 255, 255, 0.3);
            border-radius: ${pxToRem(100)};
        `,
    },
    iconImg: {
        css: css`
            width: ${pxToRem(20)};
            height: ${pxToRem(20)};
            object-fit: contain;
        `,
        hideLoading: true,
    },
    pillLabel: {
        css: css`
            font-family: ${x => x.theme.design.typography.fontFamily.base};
            font-size: ${pxToRem(14)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: 1.4;
            color: inherit;
            flex: 1;
            min-width: 0;
            ${lineEllipsisStyles(2)}
        `,
    },
};

const columnsField = getNumberTextField(fields.columns, { integer: true, min: 1 });

export const QuickActionPills: FC<QuickActionPillsProps> = ({ fields }) => {
    const { displayDynamicTitle, links: rawLinks, columns } = fields;
    const { links } = useGetLinks(rawLinks, o => o.fields.destination);
    const userFirstName = useAppSelector(state => {
        const { firstName, userName } = state.context.session;
        return firstName || userName;
    });

    const welcomeText = translate("Welcome {0}!", userFirstName);

    const pillsContainerCss = useMemo(() => {
        const columnValues = columnsField.parse(columns)?.map(n => (n != null ? `repeat(${n}, minmax(0, 1fr))` : null));
        return {
            ...quickActionPillsStyles.pillsContainer,
            css: css`
                ${quickActionPillsStyles.pillsContainer?.css}
                ${breakpointValues(columnValues, "grid-template-columns")}
            `,
        };
    }, [columns]);

    return (
        <StyledWrapper {...quickActionPillsStyles.wrapper}>
            {displayDynamicTitle && <Typography {...quickActionPillsStyles.heading}>{welcomeText}</Typography>}
            {Boolean(links?.length) && (
                <UnorderedList {...pillsContainerCss}>
                    {links.map((link, index) => {
                        const { openInNewWindow, imageUrl, iconColor, overriddenTitle } = rawLinks[index].fields;
                        const linkTitle = overriddenTitle || link.title;

                        return (
                            <ListItem key={index}>
                                {link.url ? (
                                    <Link
                                        {...quickActionPillsStyles.pill}
                                        href={link.url}
                                        target={openInNewWindow ? "_blank" : undefined}
                                    >
                                        <StyledWrapper
                                            {...quickActionPillsStyles.iconCircle}
                                            style={iconColor ? { backgroundColor: iconColor } : undefined}
                                        >
                                            {Boolean(imageUrl) && (
                                                <Img {...quickActionPillsStyles.iconImg} altText="" src={imageUrl} />
                                            )}
                                        </StyledWrapper>
                                        <StyledWrapper as="span" {...quickActionPillsStyles.pillLabel}>
                                            {linkTitle}
                                        </StyledWrapper>
                                    </Link>
                                ) : (
                                    <StyledWrapper />
                                )}
                            </ListItem>
                        );
                    })}
                </UnorderedList>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: QuickActionPills,
    definition: {
        displayName: "Quick Action Pills",
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.displayDynamicTitle,
                displayName: "Display Welcome Heading",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                variant: "toggle",
            },
            {
                ...columnsField.definition,
                displayName: "Columns",
                defaultValue: "3",
                tooltip: "The number of columns to show. Accepts a single value or an array (breakpoints) of numbers.",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                tooltip: "Add action links to display as pills",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue: string;
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }
                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        tooltip: "If no link is provided, an empty slot will be displayed.",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.imageUrl,
                        displayName: "Icon",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.iconColor,
                        displayName: "Icon Background Color",
                        editorTemplate: "ColorPickerField",
                        tooltip: "Color for the icon circle. Defaults to blue if left empty.",
                        defaultValue: "#0070CD",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
